import * as React from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { FragranceNotes } from "@/components/fragrance-notes";
import { ShopifyBuyBox } from "@/components/shopify-buy-box";
import { ChapterVialsBuyBox } from "@/components/chapter-vials-buy-box";
import { RelatedPerfumes } from "@/components/related-perfumes";
import { getProduct, type Product } from "@/lib/fragrance-data";
import { useLiveProduct } from "@/hooks/useLiveProduct";
import {
  formatMoney,
  type ShopifyProductNode,
} from "@/lib/shopify";
import { productByHandleQueryOptions } from "@/lib/shopify-queries";
import { getChapterForHandle, getCurrentChapter, isPurchasable } from "@/config/chapters";
import { ReleaseListForm } from "@/components/release-access";

import { ChapterStatus } from "@/components/ChapterStatus";
import { ChapterBuyBlock } from "@/components/ChapterBuyBlock";

import { getProductImageAlt } from "@/lib/image-alt";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";



function parseAccents(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return value
      .split(/\s*(?:•|·|\||,)\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Build a minimal Product shape from a live Shopify product when no local entry exists. */
function synthesizeProduct(live: ShopifyProductNode): Product {
  const firstVariant = live.variants.edges[0]?.node;
  const price = firstVariant
    ? formatMoney(firstVariant.price.amount, firstVariant.price.currencyCode)
    : "";
  const accents = parseAccents(live.accents?.value);
  return {
    slug: live.handle,
    name: live.title,
    subtitle: live.subtitle?.value ?? "",
    price,
    installment: live.installment?.value ?? "",
    image: live.featuredImage?.url ?? "",
    imageAlt: getProductImageAlt({
      shopifyAlt: live.featuredImage?.altText,
      handle: live.handle,
      title: live.title,
      subtitle: live.subtitle?.value,
    }),
    gallery: [],
    storyTagline: live.storyTagline?.value ?? "",
    noteLine: accents.slice(0, 3).join(" · "),
    format: "",
    batch: "",
    accents,
    storyParagraphs: [],
    storyClose: live.storyClose?.value ?? "",
    buyNote: live.buyNote?.value,
    size: "",
    ingredients: "",
    allergens: "",
    attributes: "",
    sku: live.skuDisplay?.value ?? "",
    noteSlugs: [],
    shopifyHandle: live.handle,
  };
}

export const Route = createFileRoute("/products/$slug")({
  head: ({ params }) => {
    const product = getProduct(params.slug);
    const title = product
      ? `${product.name}${product.subtitle ? ` — ${product.subtitle}` : ""} · House of Apocrypha`
      : `${params.slug.replace(/-/g, " ")} · House of Apocrypha`;
    const description = product
      ? product.searchAnchor ?? `${product.storyTagline} ${product.name} is a natural eau de parfum by House of Apocrypha.`
      : "A natural perfume by House of Apocrypha.";
    const path = `https://houseofapocrypha.com/products/${params.slug}`;
    const productImage = product?.image
      ? new URL(product.image, "https://houseofapocrypha.com").href
      : null;

    // Best-effort price parse for Product JSON-LD offers.
    const priceMatch = product?.price?.match(/[\d]+(?:\.[\d]{2})?/);
    const priceNumber = priceMatch ? priceMatch[0] : null;
    const currencyMatch = product?.price?.match(/[A-Z]{3}/);
    const currency = currencyMatch ? currencyMatch[0] : "USD";

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        ...(productImage
          ? [
              { property: "og:image", content: productImage },
              { property: "og:image:alt", content: product?.imageAlt ?? `${product?.name ?? "House of Apocrypha perfume"} — natural eau de parfum` },
              { name: "twitter:image", content: productImage },
            ]
          : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: path },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        ...(product
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Product",
                  name: product.name,
                  description,
                  image: productImage,
                  brand: { "@type": "Brand", name: "House of Apocrypha" },
                  ...(product.sku ? { sku: product.sku } : {}),
                  url: path,
                  ...(priceNumber
                    ? {
                        offers: {
                          "@type": "Offer",
                          price: priceNumber,
                          priceCurrency: currency,
                          availability: isPurchasable(
                            (getChapterForHandle(params.slug) ?? getCurrentChapter()).state,
                          )
                            ? "https://schema.org/PreOrder"
                            : "https://schema.org/SoldOut",

                          url: path,
                        },
                      }
                    : {}),
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "All Perfumes", item: "https://houseofapocrypha.com/products" },
              {
                "@type": "ListItem",
                position: 3,
                name: product?.name ?? params.slug,
                item: path,
              },
            ],
          }),
        },
      ],
    };
  },
  loader: async ({ params, context }): Promise<{ product: Product }> => {
    const local = getProduct(params.slug);
    // Key the cache by canonical slug; alias resolution happens in the loader.
    const live = await context.queryClient.ensureQueryData(
      productByHandleQueryOptions(params.slug),
    );
    if (local) return { product: local };
    if (!live) throw notFound();
    return { product: synthesizeProduct(live) };
  },

  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="text-3xl text-parchment">Fragrance not found</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          This bottle has slipped back into the archive.
        </p>
        <Link to="/" className="mt-8 inline-block eyebrow text-gold hover:text-gold-soft">
          Return home
        </Link>
      </div>
    </div>
  ),
  // No per-route errorComponent — let the root boundary handle errors so
  // transient chunk-load failures during navigation get silently retried
  // instead of flashing "This page didn't load".
  component: ProductPage,
});

function Detail({ summary, children, open = false }: { summary: string; children: React.ReactNode; open?: boolean }) {
  const [isOpen, setIsOpen] = React.useState(open);
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex w-full cursor-pointer items-center justify-between gap-4 py-5 text-left"
      >
        <span className="font-display text-xs uppercase tracking-[0.24em] text-gold">{summary}</span>
        <span
          className={`font-display text-gold transition-transform duration-300 ease-out ${
            isOpen ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-4 pb-5 text-lg leading-relaxed text-foreground/80">{children}</div>
      </motion.div>
    </div>
  );
}

function ProductPage() {
  const { product } = Route.useLoaderData() as { product: Product };
  const { copy, liveProduct } = useLiveProduct(product);

  // Hero/gallery: prefer live Shopify images when available.
  const liveImages = liveProduct?.images.edges.map((e) => e.node) ?? [];
  const heroImage = liveImages[0]?.url ?? product.image;
  const heroAlt =
    copy.searchAnchor ||
    getProductImageAlt({
      shopifyAlt: liveImages[0]?.altText,
      handle: product.slug,
      title: copy.name,
      subtitle: product.subtitle,
    });
  const extraImages = liveImages.slice(1, 5);
  const galleryLabel = `${copy.name} product photographs`;
  const imageAltFor = (altText?: string | null) =>
    getProductImageAlt({
      shopifyAlt: altText,
      handle: product.slug,
      title: copy.name,
      subtitle: product.subtitle,
    });

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <main>
        <nav className="mx-auto max-w-7xl px-6 pt-8 text-sm" aria-label="Breadcrumb">
          <Link to="/" className="text-muted-foreground transition-colors hover:text-gold">
            Home
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <Link to="/products" className="text-muted-foreground transition-colors hover:text-gold">
            The Current Chapter
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-parchment">{copy.name}</span>
        </nav>

        <section className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16">
            <div className="order-2 space-y-6 lg:order-1">
              {/* Mobile: all photos in a single horizontal row, smaller */}
              <ul
                className="-mx-6 flex list-none gap-3 overflow-x-auto px-6 pb-2 lg:hidden"
                aria-label={galleryLabel}
              >
                {[
                  ...(heroImage
                    ? [{ url: heroImage, altText: heroAlt }]
                    : []),
                  ...(product.gallery.length > 0
                    ? product.gallery.map((g: (typeof product.gallery)[number]) => ({ url: g.src, altText: g.alt || imageAltFor(null) }))
                    : extraImages.map((img) => ({ url: img.url, altText: imageAltFor(img.altText) }))),
                ].map((img, i) => (
                  <li
                    key={`${img.url}-${i}`}
                    className="aspect-[3/4] w-28 shrink-0 overflow-hidden rounded-md border border-border bg-card"
                  >
                    <ResponsiveImage
                      src={img.url}
                      alt={img.altText || imageAltFor(null)}
                      sizes={SIZES.thumb}
                      widths={[200, 400, 640]}
                      priority={i === 0}
                      pictureClassName="contents"
                      className="h-full w-full object-cover"
                    />
                  </li>
                ))}
              </ul>


              {/* Desktop: original hero + gallery */}
              {heroImage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="relative hidden overflow-hidden rounded-md border border-border bg-card lg:block"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <ResponsiveImage
                      src={heroImage}
                      alt={heroAlt}
                      width={1200}
                      height={1500}
                      sizes={SIZES.product}
                      widths={[480, 768, 1024, 1400]}
                      priority
                      pictureClassName="contents"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </motion.div>
              )}

              {(product.gallery.length > 0 || extraImages.length > 0) && (
                <div className="hidden gap-5 sm:grid-cols-2 lg:grid" role="group" aria-label={galleryLabel}>
                  {product.gallery.length > 0
                    ? product.gallery.map((image: (typeof product.gallery)[number], index: number) => (
                        <motion.figure
                          key={image.src}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.7, delay: 0.12 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden rounded-md border border-border bg-card"
                        >
                          <div className="aspect-[4/3] overflow-hidden">
                            <ResponsiveImage
                              src={image.src}
                              alt={image.alt || imageAltFor(null)}
                              width={1200}
                              height={900}
                              sizes={SIZES.product}
                              pictureClassName="contents"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          {image.caption && (
                            <figcaption className="px-4 py-3 font-display text-[0.6rem] uppercase tracking-[0.22em] text-muted-foreground">
                              {image.caption}
                            </figcaption>
                          )}
                        </motion.figure>
                      ))
                    : extraImages.map((img, index) => (
                        <motion.figure
                          key={img.url}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.7, delay: 0.12 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden rounded-md border border-border bg-card"
                        >
                          <div className="aspect-[4/3] overflow-hidden">
                            <ResponsiveImage
                              src={img.url}
                              alt={imageAltFor(img.altText)}
                              width={1200}
                              height={900}
                              sizes={SIZES.product}
                              pictureClassName="contents"
                              className="h-full w-full object-cover"
                            />
                          </div>
                        </motion.figure>
                      ))}
                </div>
              )}

            </div>


            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="order-1 flex flex-col lg:order-2"
            >
              <p className="eyebrow">House of Apocrypha</p>
              {(() => {
                // Strip a redundant subtitle from the H1 (e.g. Shopify title
                // "Hidden - a Violet Fougère" with subtitle "A Violet Fougère").
                const sub = copy.subtitle?.trim() ?? "";
                const nameRaw = copy.name ?? "";
                let displayName = nameRaw;
                if (sub) {
                  const re = new RegExp(
                    "\\s*[-—–:·]\\s*" + sub.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\s*$",
                    "i",
                  );
                  displayName = nameRaw.replace(re, "").trim() || nameRaw;
                }
                return (
                  <>
                    <h1 className="mt-4 text-4xl text-parchment sm:text-5xl">{displayName}</h1>
                    {sub && (
                      <p className="mt-2 font-serif text-2xl italic text-gold">{sub}</p>
                    )}
                  </>
                );
              })()}

              {copy.installment && (
                <p className="mt-1 text-sm text-muted-foreground">{copy.installment}</p>
              )}

              {copy.accents.length > 0 && (
                <>
                  <p className="mt-7 max-w-md text-sm uppercase tracking-[0.18em] text-muted-foreground">
                    Key accents
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {copy.accents.map((a: string) => (
                      <span
                        key={a}
                        className="rounded-full border border-border px-3 py-1 text-sm text-foreground/80"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {product.slug === "chapter-vials" ? (
                <>
                  {/* Vials UI is retained in full and gated by
                      `vialsAvailable` in src/config/chapters.ts. */}
                  {getCurrentChapter().vialsAvailable ? (
                    <>
                      <div className="mt-8 border-t border-border pt-6">
                        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 font-display text-xs uppercase tracking-[0.2em]">
                          <dt className="text-muted-foreground">Chapter</dt>
                          <dd className="text-parchment">{getCurrentChapter().label}</dd>
                          <dt className="text-muted-foreground">Dispatch</dt>
                          <dd className="text-gold">Ships Fridays</dd>
                          <dt className="text-muted-foreground">Vial size</dt>
                          <dd className="text-parchment">2 ml</dd>
                        </dl>
                        <p className="mt-4 font-serif text-sm italic text-muted-foreground">
                          Choose one fragrance for each vial before adding to cart.
                        </p>
                      </div>

                      <ChapterVialsBuyBox slug={product.slug} fallbackPrice={product.price} />
                    </>
                  ) : (
                    <div className="mt-8 border-t border-border pt-8">
                      <h2 className="font-display text-sm uppercase tracking-[0.22em] text-parchment">
                        Chapter Vials are available while a chapter is open.
                      </h2>
                      <p className="mt-4 max-w-md font-serif text-lg italic leading-relaxed text-muted-foreground">
                        The Fall chapter is closed. Vials return with the Winter
                        chapter.
                      </p>
                      <div className="mt-8">
                        <h3 className="font-display text-xs uppercase tracking-[0.24em] text-gold">
                          Be notified when the next chapter opens.
                        </h3>
                        <div className="mt-5">
                          <ReleaseListForm compact />
                        </div>
                      </div>
                    </div>
                  )}


                  <div className="mt-10">
                    <Detail summary="About Chapter Vials" open>
                      <p>Choose the fragrances you wish to experience from the current chapter.</p>
                      <p>
                        Chapter Vials are 2 ml samples of House of Apocrypha
                        perfumes, available only while the chapter window is
                        open. They are not a permanent sample range, and they
                        are not kept in constant stock. When the chapter closes,
                        the vials close with it.
                      </p>
                      <p>
                        Each vial offers a small encounter: enough to wear the
                        fragrance more than once, to let it move through skin,
                        weather, mood, and hour.
                      </p>
                    </Detail>

                    <Detail summary="The Memory Archive">
                      <p className="font-serif italic text-gold/90">
                        Ink, hushed halls, weather, obsession.
                      </p>
                      <p>
                        The Memory Archive gathers five intimate fragrances:
                        stolen glances, paper margins, cardamom warmth, green
                        shadows, and the sort of longing best left
                        unsupervised.
                      </p>
                      <p>For those who remember things that never quite happened.</p>
                    </Detail>

                    <Detail summary="Current Chapter Fragrances">
                      <ul className="space-y-2">
                        <li>
                          <span className="text-parchment">The Archivist</span> — wine-soaked oak, scorched paper, and vanilla dust
                        </li>
                        <li>
                          <span className="text-parchment">A Gentle Madness</span> — fevered absinthe, strange herbs, and spiced delirium
                        </li>
                        <li>
                          <span className="text-parchment">Velvet Sugar</span> — sugared softness, pale woods, and a little trouble
                        </li>
                        <li>
                          <span className="text-parchment">Still</span> — lavender frost, green fern, and powdered winter air
                        </li>
                        <li>
                          <span className="text-parchment">Hidden</span> — a floral smoulder veiled in green
                        </li>
                      </ul>
                    </Detail>
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const chapter =
                      getChapterForHandle(product.slug) ?? getCurrentChapter();
                    return (
                      <>
                        <ChapterStatus chapter={chapter} />
                        <ChapterBuyBlock
                          chapter={chapter}
                          product={{
                            slug: product.slug,
                            price: product.price,
                            buyNote: copy.buyNote,
                          }}
                        />
                      </>
                    );
                  })()}

                  <div className="mt-10">
                    {(copy.storyTagline || copy.storyParagraphs.length > 0 || copy.storyClose) && (
                      <Detail summary="Fragrance story" open>
                        {copy.storyTagline && (
                          <p className="font-serif text-xl italic text-gold/90">{copy.storyTagline}</p>
                        )}
                        {copy.storyParagraphs.map((p: string, i: number) => (
                          <p key={i}>{p}</p>
                        ))}
                        {copy.storyClose && (
                          <p className="font-serif text-xl italic text-parchment">{copy.storyClose}</p>
                        )}
                      </Detail>
                    )}

                    <Detail summary="Size & presentation">
                      <p>30 mL black glass bottle, presented in a black box.</p>
                    </Detail>


                    <Detail summary="The Vial Promise">
                      <p>
                        Every bottle arrives with a companion vial of the same
                        fragrance. The vial travels outside the sealed package.
                        Wear it for three days. If the chapter is not yours,
                        contact us within fourteen days of delivery and return
                        the bottle unopened, in its original sealed packaging.
                        The vial remains with you.
                      </p>

                    </Detail>

                    {copy.ingredients && (
                      <Detail summary="Ingredients">
                        <p>{copy.ingredients}</p>
                        {copy.allergens && (
                          <p className="text-base text-muted-foreground">{copy.allergens}</p>
                        )}
                        {copy.attributes && (
                          <p className="text-base text-muted-foreground">{copy.attributes}</p>
                        )}
                        {copy.sku && (
                          <p className="text-sm uppercase tracking-[0.18em] text-muted-foreground/70">
                            SKU: {copy.sku}
                          </p>
                        )}
                      </Detail>
                    )}
                  </div>
                </>
              )}

            </motion.div>
          </div>
        </section>

        {copy.noteCards.length > 0 && (
          <>
            <div className="ornament">
              <span>✶</span>
            </div>

            <FragranceNotes cards={copy.noteCards} />
          </>
        )}

        <RelatedPerfumes currentSlug={product.slug} />
      </main>

      <SiteFooter />
    </div>
  );
}
